import React, { useState, useRef, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SUGGESTIONS = [
  'Best spiritual destinations?',
  'Plan a retreat for me',
  'Yoga & Meditation centres',
  'Ayurveda healing in Kerala',
  'Varanasi pilgrimage guide',
  'Best time to visit India',
];

const TypewriterText = ({ text, onComplete }) => {
  const [displayedText, setDisplayedText] = useState('');
  
  useEffect(() => {
    let i = 0;
    setDisplayedText('');
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayedText(text.substring(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
        if (onComplete) onComplete();
      }
    }, 15);
    return () => clearInterval(timer);
  }, [text, onComplete]);

  return <>{displayedText}</>;
};

// Generate a persistent session ID per browser tab
const isAuth = localStorage.getItem('user_authenticated') === 'true' || localStorage.getItem('admin_authenticated') === 'true';
const SESSION_ID = (isAuth ? 'bot_reg_' : 'bot_gst_') + Math.random().toString(36).slice(2, 10);

export default function Chatbot() {
  // eslint-disable-next-line no-unused-vars
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: 'bot',
      text: 'Namaste 🙏 I\'m Darshan, your Sacred Travel Guide powered by AI. How can I illuminate your spiritual journey through India today?',
      time: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [pulse, setPulse] = useState(true);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, typing]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      setPulse(false);
    }
  }, [open]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || typing) return;
    setInput('');

    const userMsg = { from: 'user', text: msg, time: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setTyping(true);

    try {
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: SESSION_ID, text: msg })
      });

      if (!res.ok) throw new Error('API error');
      const data = await res.json();

      await new Promise(resolve => setTimeout(resolve, 2000));

      setMessages(prev => [...prev, {
        from: 'bot',
        text: data.text,
        time: new Date(),
        isNew: true
      }]);
    } catch {
      setMessages(prev => [...prev, {
        from: 'bot',
        text: 'I\'m having trouble connecting right now 🙏 Please try again in a moment, or contact us at info@spiritualjourneys.in',
        time: new Date()
      }]);
    } finally {
      setTyping(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const fmt = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {/* ========================= */}
      {/* CHAT WINDOW */}
      {/* ========================= */}
      {/* BACKDROP */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            background: 'transparent'
          }}
        />
      )}

      {open && (
        <div
          className="chatbot-container"
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '24px',
            width: '370px',
            maxWidth: 'calc(100vw - 32px)',
            height: '560px',
            maxHeight: 'calc(100vh - 120px)',
            borderRadius: '20px',
            background: '#FAF6F0',
            border: '1px solid rgba(214,179,106,0.4)',
            boxShadow: '0 32px 80px rgba(0,0,0,0.2), 0 0 40px rgba(214,179,106,0.15)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999,
            overflow: 'hidden',
            animation: 'chatSlideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)'
          }}>

          {/* HEADER */}
          <div style={{
            padding: '16px 18px',
            background: 'linear-gradient(135deg, rgba(214,179,106,0.15), rgba(214,179,106,0.04))',
            borderBottom: '1px solid rgba(214,179,106,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              background: '#ffffff',
              border: '1px solid rgba(214,179,106, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(214,179,106,0.4)',
              overflow: 'hidden'
            }}>
              <img src="/images/new-logo.png" alt="Mystic Routes" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#8a641c', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.03em' }}>
                Darshan
              </div>
              <div style={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4caf83', display: 'inline-block', boxShadow: '0 0 6px rgba(76,175,131,0.5)' }} />
                Online · Powered by Mystic Routes
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: 'rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: '8px',
                color: 'rgba(0,0,0,0.5)',
                cursor: 'pointer',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.1)'; e.currentTarget.style.color = '#000'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.05)'; e.currentTarget.style.color = 'rgba(0,0,0,0.5)'; }}
            >
              <X size={14} />
            </button>
          </div>

          {/* MESSAGES */}
          <div style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(214,179,106,0.2) transparent'
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.from === 'user' ? 'flex-end' : 'flex-start',
                animation: 'msgFadeIn 0.3s ease'
              }}>
                <div style={{
                  maxWidth: '82%',
                  padding: '10px 14px',
                  borderRadius: msg.from === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.from === 'user'
                    ? 'linear-gradient(135deg, #d6b36a, #b8943f)'
                    : '#ffffff',
                  border: msg.from === 'user' ? 'none' : '1px solid rgba(214,179,106,0.3)',
                  color: msg.from === 'user' ? '#ffffff' : '#333333',
                  fontSize: '0.82rem',
                  lineHeight: 1.6,
                  fontWeight: msg.from === 'user' ? 600 : 400,
                  whiteSpace: 'pre-line',
                  boxShadow: msg.from === 'user' ? '0 4px 12px rgba(214,179,106,0.3)' : '0 2px 8px rgba(0,0,0,0.03)'
                }}>
                  {msg.isNew && msg.from === 'bot' ? (
                    <TypewriterText text={msg.text} onComplete={() => { msg.isNew = false; }} />
                  ) : (
                    msg.text
                  )}
                </div>
                <span style={{ fontSize: '0.62rem', color: 'rgba(0,0,0,0.3)', marginTop: '3px', paddingLeft: '4px', paddingRight: '4px' }}>
                  {fmt(msg.time)}
                </span>
              </div>
            ))}

            {/* TYPING INDICATOR */}
            {typing && (
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{
                  padding: '10px 16px',
                  borderRadius: '16px 16px 16px 4px',
                  background: '#ffffff',
                  border: '1px solid rgba(214,179,106,0.3)',
                  display: 'flex',
                  gap: '5px',
                  alignItems: 'center'
                }}>
                  {[0, 1, 2].map(i => (
                    <span key={i} style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#d6b36a',
                      display: 'inline-block',
                      animation: `typingDot 1.2s ${i * 0.2}s infinite`
                    }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* SUGGESTIONS */}
          {messages.length <= 2 && (
            <div style={{
              padding: '0 14px 10px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px'
            }}>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(s)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '20px',
                    border: '1px solid rgba(214,179,106,0.4)',
                    background: '#ffffff',
                    color: '#8a641c',
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    letterSpacing: '0.02em',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#faf6f0'; e.currentTarget.style.borderColor = '#d6b36a'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = 'rgba(214,179,106,0.4)'; }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* INPUT */}
          <div style={{
            padding: '12px 14px',
            borderTop: '1px solid rgba(214,179,106,0.2)',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-end',
            background: '#ffffff'
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask Mystic Routes anything..."
              rows={1}
              disabled={typing}
              style={{
                flex: 1,
                background: '#FAF6F0',
                border: '1px solid rgba(214,179,106,0.3)',
                borderRadius: '12px',
                padding: '10px 14px',
                color: '#333333',
                fontSize: '0.82rem',
                resize: 'none',
                outline: 'none',
                fontFamily: 'Inter, sans-serif',
                lineHeight: 1.5,
                maxHeight: '80px',
                overflowY: 'auto',
                transition: 'border-color 0.2s',
                opacity: typing ? 0.6 : 1
              }}
              onFocus={e => { e.target.style.borderColor = 'rgba(214,179,106,0.8)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(214,179,106,0.3)'; }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || typing}
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '12px',
                border: 'none',
                background: (input.trim() && !typing)
                  ? 'linear-gradient(135deg, #d6b36a, #b8943f)'
                  : 'rgba(0,0,0,0.05)',
                color: (input.trim() && !typing) ? '#ffffff' : 'rgba(0,0,0,0.2)',
                cursor: (input.trim() && !typing) ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.2s',
                boxShadow: (input.trim() && !typing) ? '0 4px 12px rgba(214,179,106,0.4)' : 'none'
              }}
            >
              <Send size={15} />
            </button>
          </div>
        </div>
      )}

      {/* ========================= */}
      {/* FLOATING BUTTON */}
      {/* ========================= */}
      <button
        className="floating-widget floating-widget-right"
        onClick={() => setOpen(o => !o)}
        title="Mystic Routes — Sacred Travel Guide"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '70px',
          height: '70px',
          borderRadius: '50%',
          border: '2px solid rgba(214,179,106, 0.8)',
          background: '#ffffff',
          color: '#1a1508',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          boxShadow: '0 10px 40px rgba(0,0,0,0.4), 0 2px 10px rgba(214,179,106,0.3)',
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s',
          transform: open ? 'scale(1.05) rotate(15deg)' : 'scale(1)'
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.transform = 'scale(1.15) translateY(-5px)'; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {open ? <X size={26} color="#333" /> : <img src="/images/new-logo.png" alt="Chatbot Guide" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />}

        {/* PULSE RING */}
        {pulse && !open && (
          <span style={{
            position: 'absolute',
            inset: '-6px',
            borderRadius: '50%',
            border: '2px solid rgba(214,179,106,0.8)',
            animation: 'pulseRing 2s infinite'
          }} />
        )}

        {/* ONLINE DOT */}
        {!open && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            background: '#4caf83',
            border: '2px solid #1a1508',
            boxShadow: '0 0 8px #4caf83'
          }} />
        )}
      </button>

      {/* ========================= */}
      {/* KEYFRAME STYLES */}
      {/* ========================= */}
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        @keyframes pulseRing {
          0%   { transform: scale(1);   opacity: 0.8; }
          70%  { transform: scale(1.3); opacity: 0;   }
          100% { transform: scale(1.3); opacity: 0;   }
        }
        @keyframes typingDot {
          0%, 60%, 100% { transform: translateY(0);    opacity: 0.4; }
          30%            { transform: translateY(-5px); opacity: 1;   }
        }
        @keyframes msgFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
      `}</style>
    </>
  );
}
